import Header from "@/components/Header";
import Testimonials from "@/components/Testimonials";
import UserPersonas from "@/components/download/UserPersonas";
import Footer from "@/components/Footer";
import ModernNoticeBox from "@/components/download/ModernNoticeBox";
import EnhancedFAQ from "@/components/download/EnhancedFAQ";
import InteractiveSetupProcess from "@/components/download/InteractiveSetupProcess";
import ModernAppStoreButtons from "@/components/download/ModernAppStoreButtons";
import { motion } from "framer-motion";
import HealthMonitoringBento from "@/components/download/HealthMonitoringBento";
import AppExperience from "@/components/download/AppExperience";
import CircularFeatureOrbit from "../components/CircularFeatureOrbit";
import FinalCallToAction from "@/components/download/FinalCallToAction";
import { useTestimonialsData } from "@/components/data/testimonialsData";
import SectionHeader from "@/components/SectionHeader";
import HeroSection from "@/components/download/DownloadHeroSection";
import { useRef, useState, useEffect } from "react";
import DownloadDivider from "@/components/download/DownloadDivider";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";


const DownloadPage = () => {
    // Structured data for SEO
    const structuredData = {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: "دلبند",
        operatingSystem: "Android, iOS",
        applicationCategory: "HealthApplication",
        description: "اپلیکیشن دلبند، ابزار هوشمند پایش سلامت برای خانواده‌ها، سالمندان، کودکان و سازمان‌هاست. همراه با ردیابی موقعیت، تحلیل خواب و استرس، و هشدارهای پیشگیرانه.",
        offers: {
            "@type": "Offer",
            price: "0",
            priceCurrency: "IRR",
            availability: "https://schema.org/InStock",
            url: "https://delband.com/download",
        },
        publisher: {
            "@type": "Organization",
            name: "دلبند",
            url: "https://delband.com",
        },
    };

    const { t } = useTranslation(["CircularFeatureOrbit", "FAQ", "Testimonials"]);
    const { TestimonialDataDownload } = useTestimonialsData();

    // استفاده از cubic-bezier مشخص شده برای حرکت طبیعی
    const fadeInUp = {
        initial: { opacity: 0, y: 20 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.6, ease: [0.4, 0.0, 0.2, 1] as const },
    };

    const [triggerAnimation, setTriggerAnimation] = useState(0);

    const appStoreRef = useRef(null);
    const setupProcessRef = useRef(null);

    const scrollToAppStore = () => {
        if (appStoreRef.current) {
            setTimeout(() => {
                (appStoreRef.current as unknown as HTMLElement).scrollIntoView({
                    behavior: "instant",
                    block: "start",
                });
            }, 300);
        }
    };

    const scrollToSetupProcess = () => {
        if (setupProcessRef.current) {
            setTimeout(() => {
                (setupProcessRef.current as unknown as HTMLElement).scrollIntoView({
                    behavior: "instant",
                    block: "start",
                });
            }, 300);
        }
    };

    return (
        <>
            <Helmet>
                <title>دلبند | دانلود اپلیکیشن پایش هوشمند</title>
                <meta name="description" content="اپلیکیشن دلبند، همراه هوشمند سلامت خانواده و سازمان‌هاست. با اتصال به ساعت یا حلقه هوشمند، وضعیت سلامت، موقعیت و استرس را پایش کنید. دانلود مستقیم نسخه اندروید و iOS." />
                <meta name="keywords" content="دلبند, دانلود اپلیکیشن دلبند, اپلیکیشن سلامت, پایش سلامت, ساعت هوشمند, ردیاب کودک, سلامت سازمانی, سلامت خانواده, دل بند, فناوری سلامت, health app" />
                <link rel="canonical" href="https://delband.com/download" />
                <meta property="og:title" content="دلبند | دانلود اپلیکیشن سلامت هوشمند" />
                <meta property="og:description" content="اپلیکیشن دلبند، پلتفرم سلامت هوشمند برای پایش لحظه‌ای سلامت، موقعیت و آرامش خانواده و کارکنان. دانلود رایگان نسخه موبایل از دلبند." />
                <meta property="og:url" content="https://delband.com/download" />
                <meta property="og:type" content="website" />
                <script type="application/ld+json">
                    {JSON.stringify(structuredData)}
                </script>
            </Helmet>
            <div className="min-h-screen overflow-hidden bg-[#F8F9FC]">
                <Header />
                <HeroSection downloadClick={scrollToAppStore} setupClick={scrollToSetupProcess} />
                {/* بخش قهرمان پیشرفته */}
                <main className="container mx-auto px-2 pb-20 pt-4 max-w-7xl">
                    {/* جعبه اطلاع‌رسانی */}
                    <motion.section {...fadeInUp} className="pt-6 pb-4">
                        <ModernNoticeBox />
                    </motion.section>
                    {/* دکمه‌های مدرن فروشگاه اپلیکیشن با تصویر ساعت */}
                    <motion.section className="py-16 relative" {...fadeInUp}>
                        <div className="container px-4 sm:px-10 relative py-12 mx-auto">
                            <div className="relative z-10" id="app-store-buttons">
                                <motion.div
                                    key={triggerAnimation}
                                    ref={appStoreRef}
                                    initial={{ opacity: 0 }}
                                    whileInView={{ opacity: 1 }}
                                    transition={{ duration: 0.6, ease: "easeOut" }}
                                    viewport={{ once: false, margin: "-100px" }}
                                >
                                    <div ref={appStoreRef} className="scroll-mt-32">
                                        <ModernAppStoreButtons />
                                    </div>
                                </motion.div>
                            </div>
                        </div>
                    </motion.section>
                    {/* بخش تجربه اپلیکیشن */}
                    <motion.section {...fadeInUp}>
                        <AppExperience />
                    </motion.section>
                    {/* بخش فرآیند راه‌اندازی تعاملی */}
                    <motion.section {...fadeInUp} id="InteractiveSetupProcess">
                        <div className="py-16 md:py-28 relative z-10 mx-auto container">
                            <motion.div
                                key={triggerAnimation}
                                ref={setupProcessRef}
                                initial={{ opacity: 0 }}
                                whileInView={{ opacity: 1 }}
                                transition={{ duration: 0.6, ease: "easeOut" }}
                                viewport={{ once: false, margin: "-100px" }}
                            >
                                <div ref={setupProcessRef} className="scroll-mt-32">
                                    <InteractiveSetupProcess />
                                </div>
                            </motion.div>
                        </div>
                    </motion.section>

                    {/* بخش بنتو پایش سلامت */}
                    <motion.section {...fadeInUp}>
                        <HealthMonitoringBento />
                    </motion.section>
                    {/* بخش ویژگی‌های اضافی */}
                    <motion.section className="py-16 md:py-28 " {...fadeInUp}>
                        <div className="text-center mb-4 px-2">
                            <SectionHeader
                                regularText={t("sectionHeader.regularText", { ns: "CircularFeatureOrbit" })}
                                highlightedText={t("sectionHeader.highlightedText", { ns: "CircularFeatureOrbit" })}
                                descriptionHighlight={t("sectionHeader.descriptionHighlight", { ns: "CircularFeatureOrbit" })}
                                descriptionText={t("sectionHeader.descriptionText", { ns: "CircularFeatureOrbit" })}
                            />
                        </div>
                        <CircularFeatureOrbit />
                    </motion.section>
                    {/* بخش چه کسانی از دلبند استفاده می‌کنند */}
                    <motion.section {...fadeInUp}>
                        <div className="container mx-auto sm:px-10 px-4">  
                        <UserPersonas />
                        </div>
                    </motion.section>
                    {/* بخش نظرات کاربران با استایل پیشرفته */}
                    <motion.section className="py-16 md:py-28 relative" {...fadeInUp}>
                        {/* پس‌زمینه گرادیان پیشرفته */}
                        <div className="absolute inset-0 overflow-hidden">
                            <motion.div
                                className="absolute top-1/4 left-1/4 w-[400px] h-[400px] rounded-full blur-[100px] opacity-10"
                                animate={{
                                    scale: [1, 1.1, 1],
                                    opacity: [0.1, 0.05, 0.1],
                                }}
                                transition={{
                                    duration: 12,
                                    repeat: Infinity,
                                    repeatType: "reverse",
                                    ease: [0.4, 0.0, 0.2, 1],
                                }}
                            />
                        </div>

                        <div className="container mx-auto px-2 sm:px-10 relative z-10">
                            <Testimonials data={TestimonialDataDownload} translationNamespace="Testimonials" />
                        </div>
                    </motion.section>
                    {/* بخش سوالات متداول پیشرفته */}
                    <motion.section className="mx-auto" {...fadeInUp}>
                        <div className="container mx-auto px-4 sm:px-10">
                            <div className="text-center">
                                <SectionHeader
                                    regularText={t("sectionHeader.regularText", { ns: "FAQ" })}
                                    highlightedText={t("sectionHeader.highlightedText", { ns: "FAQ" })}
                                    descriptionHighlight={t("sectionHeader.descriptionHighlight", { ns: "FAQ" })}
                                    descriptionText={t("sectionHeader.descriptionText", { ns: "FAQ" })}
                                />
                            </div>

                            <EnhancedFAQ />
                        </div>
                    </motion.section>
                    <motion.section className="pt-32" {...fadeInUp}>
                        <FinalCallToAction />
                    </motion.section>
                </main>
                <Footer />
            </div>
        </>
    );
};

export default DownloadPage;
